import {Component, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {differenceInHours} from 'date-fns';
import {ngxCsv} from 'ngx-csv';
import {Content, createDiv, createIcon, DevicesService, LangService, LoaderService, NavigateService, TableComponent, TableConfig, TableDataTransformer, TopButton} from 'ngx-satoris';
import {QueryService} from 'src/app/shared/services/query.service';
import {CurrencySign, RequestFilter, RequestState, RequestStatus, RequestWithDuplicata, RequestSubState, GlobalOperationType, TransformedRequestWithDuplicata} from '../../shared/models/request';
import {ApiService} from '../../shared/services/api.service';
import {enumToArrayObject, enumStringToArrayObject} from '../../shared/utils/enumToArrayObject';
import {DocumentType, DocumentTypeInTablePattern, UserPermission, UserPlatformRole} from '../../shared/models/user';
// Removed unused imports after environment.type cleanup
import {RequestService} from 'src/app/shared/services/request.service';
import {ScanDocumentService} from 'src/app/shared/services/scan-document.service';
import {SyncService} from 'src/app/shared/services/sync.service';
import {PaymentService} from '../../shared/services/payment.service';
import {QueueService} from '../../shared/services/queue.service';
import {ORIGINAL_TABLE_REQUESTS_CONFIG, TABLE_REQUESTS_PRE_FILTERS, TableRequestsFilterEnum, filterOverrideFunctions} from 'src/app/shared/models/table-config';
import {buildAdminRequestsTableConfig, AdminRequestsTableDeps, searchApplications, handleAdminRequestsOnQueryChange, hasUiPerm} from 'src/app/shared/models/default-filter-strategy';
import {FilterWithType} from 'ngx-satoris/lib/shared/models/filters';
import {PaginationQuery, SortQuery} from 'ngx-satoris/lib/shared/models/params';
import {QueryParams} from 'src/app/shared/types';
import {UiPermission} from 'src/app/shared/models/permission';

@Component({
  selector: 'app-admin-payment',
  templateUrl: './admin-request.component.html',
  styleUrls: ['./admin-request.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AdminRequestComponent implements OnInit {
  filteredData: RequestWithDuplicata[] = [];
  loadedData: RequestWithDuplicata[] = [];
  firstCall = false;
  newFilter = true;
  timer: NodeJS.Timeout;
  dataFromDates: RequestWithDuplicata[] = [];
  userId: number;
  totalCount: number;
  state = RequestState;
  requestFilterOption = enumStringToArrayObject(RequestFilter);
  requestSinceOption = [
    {name: 'global.noFilter', value: null},
    {name: this.lang.transform('time.day', {value: 1}), value: '1d'},
    {name: this.lang.transform('time.days', {value: 3}), value: '3d'},
    {name: this.lang.transform('time.week', {value: 1}), value: '1w'},
    {name: this.lang.transform('time.weeks', {value: 2}), value: '2w'},
    {name: this.lang.transform('time.month', {value: 1}), value: '1m'},
    {name: this.lang.transform('time.months', {value: 2}), value: '2m'},
    {name: this.lang.transform('time.months', {value: 6}), value: '6m'},
    {name: this.lang.transform('time.year', {value: 1}), value: '1y'}
  ];
  stateFilterOption: {value: string, name: string}[];
  stateExtensionFilterOption: {value: string, name: string}[];
  documentTypeOptions: {value: string, name: string}[] = [];
  operationIdFilterOption = enumStringToArrayObject(GlobalOperationType, true);
  hasFilter: boolean;
  params: QueryParams;
  CurrencySign = CurrencySign;
  private lastFilters: FilterWithType[] = [];
  private lastSort: SortQuery | undefined;

  tableId: string = 'table-requests';
  config: TableConfig;
  readonly initialConfig: TableConfig = {...ORIGINAL_TABLE_REQUESTS_CONFIG};
  hasUiPerm = hasUiPerm;

  topButtons: TopButton[] = [
    ...(hasUiPerm.call(this, UiPermission.SHOW_QR_SCANNER_BTN) ? [
      {label: this.lang.transform('dropdown.qr'), icon: 'qrcode', action: () => this.nav.to('scan-qr'), style: {backgroundColor: 'white', color: 'black'}}
    ] : []),
    ...(this.api.hasPerm(UserPermission.ALLOW_CONTROL) ? [{label: this.lang.transform('see.qr_list'), icon: 'table-list', action: () => this.goToAllQr(), style: {backgroundColor: 'white', color: 'black'}}] : []) as any,
    {label: this.lang.transform('payments.download_csv'), icon: 'download', action: () => this.downloadCSV()}
  ];

  tableDataTransformer: TableDataTransformer<TransformedRequestWithDuplicata>;
  filterOverrideFunctions = filterOverrideFunctions;

  @ViewChild('smartTable') smartTable: TableComponent;

  constructor(public loader: LoaderService,
    public nav: NavigateService,
    public api: ApiService,
    public query: QueryService,
    public device: DevicesService,
    public request: RequestService,
    public scanDocument: ScanDocumentService,
    public sync: SyncService,
    private route: ActivatedRoute,
    private payment: PaymentService,
    private router: Router,
    private queue: QueueService,
    private lang: LangService) {
    this.userId = +this.router.getCurrentNavigation().extras.state;
  }

  ngOnInit(): void {
    this.queue.onNavDeleteOfflineQueueId = undefined;
    this.route.queryParams.subscribe((params: any) => {
      this.hasFilter = Object.keys(params).length > 0;
      if(this.api.isOnline) {
        this.params = params;
        this.filteredData = [];
        if(!this.smartTable) {
          return this.launchFilter();
        } else { // Apply mrz filters if smartTable is already initialized
          const mrzFilters: Partial<Record<TableRequestsFilterEnum, string | null>> = {};
          TABLE_REQUESTS_PRE_FILTERS.forEach(([filterEnum, paramKey]) => {
            if(params[paramKey]) {
              mrzFilters[filterEnum] = params[paramKey];
            }
          });
          if(Object.keys(mrzFilters).length > 0) {
            this.smartTable.setFilters(mrzFilters);
          }
        }
      }
    });
    this.stateFilterOption = this.generateStatusFilterOptions(false);
    this.stateExtensionFilterOption = this.generateStatusFilterOptions(true);

    this.stateFilterOption.map(filter => filter.name = !filter.value ? this.lang.transform('global.noFilter') : this.lang.transform('payment_status.DYN.' + filter.value));
    this.stateExtensionFilterOption.map(filter => filter.name = !filter.value ? this.lang.transform('global.noFilter') : this.lang.transform('payment_status.extension.DYN.' + filter.value));
    this.operationIdFilterOption.map(filter => filter.name = !filter.value ? this.lang.transform('global.noFilter') : this.lang.transform(filter.value));
    this.stateFilterOption = this.stateFilterOption.filter(x => x.name !== this.lang.transform('request_state.' + RequestStatus[RequestState.DUPLICATED]) && x.name !== this.lang.transform('request_state.' + RequestStatus[RequestState.REFUNDED]));
    this.stateExtensionFilterOption = this.stateExtensionFilterOption.filter(x => x.name !== this.lang.transform('request_state.' + RequestStatus[RequestState.DUPLICATED]) && x.name !== this.lang.transform('request_state.' + RequestStatus[RequestState.REFUNDED]));
    this.documentTypeOptions = Object.values(DocumentType).filter(t => DocumentTypeInTablePattern.test(t)).map((type: DocumentType) => ({
      name: type,
      value: type
    }));

    this.setupTableDataTransformer();
  }

  setupTableDataTransformer() {
    // Build env-specific transformer/config via strategy to support multiple environments.
    const deps: AdminRequestsTableDeps = {
      api: this.api,
      request: this.request,
      lang: this.lang,
      userId: this.api.userInfo.id,
      params: this.params,
      createLockContent: (row: TransformedRequestWithDuplicata) => this.createLockContent(row),
      createUrgentContent: (row: TransformedRequestWithDuplicata) => this.createUrgentContent(row),
      createDelayedContent: (row: TransformedRequestWithDuplicata) => this.createDelayedContent(row),
      createPaymentStatusContent: (row: TransformedRequestWithDuplicata) => this.createPaymentStatusContent(row)
    };
    const cfg = buildAdminRequestsTableConfig(deps);
    this.tableDataTransformer = cfg.dataTransformer as any;
    this.config = cfg;
  }

  private createLockContent(requestData: TransformedRequestWithDuplicata): Content {
    if(!requestData.lock) return {value: ''};

    const isMyLock = requestData.assignedUserId === this.api.userInfo.id;
    const lockColor = isMyLock ? '#489F46' : '#FF823C';
    const tooltipType = isMyLock ? '.me' : '.other';
    const officerDisplay = this.sync.userDisplay(+requestData.assignedUserId) || '';

    return {
      value: '',
      style: {textAlign: 'center'},
      tooltip: this.lang.transform(`tooltip.tableLock${tooltipType}`, {officer: officerDisplay}),
      prefix: [createIcon('book-open-reader', {
        style: {color: lockColor, alignItems: 'center'},
        tooltip: 'tooltip.tableLockTitle'
      })]
    };
  }

  private createUrgentContent(requestData: TransformedRequestWithDuplicata): Content {
    return {
      value: '',
      prefix: requestData.isUrgent ? [createIcon('triangle-exclamation', {
        tooltip: 'tooltip.urgent',
        className: 'state-color',
        style: {color: '#B50000'}
      })] : null
    };
  }

  private createDelayedContent(requestData: TransformedRequestWithDuplicata): Content {
    return {
      value: '',
      prefix: requestData.isDelayed ? [createIcon('clock', {
        tooltip: 'tooltip.delayed',
        className: 'state-color',
        style: {color: '#FF4136'}
      })] : null
    };
  }

  private createPaymentStatusContent(requestData: TransformedRequestWithDuplicata): Content {
    return {
      value: requestData.transformedState,
      tooltip: 'tooltip.tableState',
      prefix: [createDiv({
        className: 'state-color',
        style: {backgroundColor: requestData.iconColor}
      })],
      suffix: this.request.requestHasBeenCorrected(requestData)
        ? [createIcon('spell-check', {
          className: 'ml_4',
          tooltip: 'tooltip.hasBeenCorrected'
        })]
        : undefined
    };
  }

  offlineLoader() {
    this.loader.loading(true, {type: 'warn', message: this.lang.transform('no.connection')});
  }

  downloadCSV() {
    if(!this.api.env.csvDownloadLimit) {
      this.downloadTableCSV();
      return;
    }
    this.loader.loading(true, {
      type: 'question',
      message: this.lang.transform('payments.download_choice'),
      btnLabel: 'payments.download_table',
      btnColor: 'secondary',
      additionalButtons: [{
        label: this.lang.transform('payments.download_filtered', {value: this.api.env.csvDownloadLimit}),
        icon: 'cloud-arrow-down',
        color: 'primary',
        border: true,
        closeOnAction: true,
        action: () => this.downloadFilteredCSV()
      }],
      custom: {closeBtnLabel: 'global.cancel'}
    }).then((chooseTable) => {
      if(chooseTable) this.downloadTableCSV();
    });
  }

  private downloadTableCSV() {
    const visibleColumns = Object.entries(this.config.table)
      .filter(([, column]) => column.visible)
      .map(([, column]) => column);

    if(!visibleColumns.length) {
      this.loader.loading(true, {type: 'warn', message: this.lang.transform('one.payment')});
      return;
    }
    const headers = visibleColumns.map(col => this.lang.transform('smartTable.' + col.value));
    const rowCount = visibleColumns[0].data.length;
    const csvData = [];

    for(let i = 0; i < rowCount; i++) {
      const row: {[header: string]: string} = {};
      visibleColumns.forEach((col, index) => {
        const colKey = col.value;
        if(colKey === 'isUrgent' || colKey === 'isDelayed') {
          const cellData = col.data[i];
          row[headers[index]] = cellData?.prefix ? 'Yes' : 'No';
          return;
        }
        const cellData = col.data[i];
        row[headers[index]] = cellData?.value?.toString() || '';
      });
      csvData.push(row);
    }
    new ngxCsv(csvData, 'applications_report', {showTitle: true, title: 'Applications Report', headers});
  }

  private downloadFilteredCSV() {
    const limit = this.api.env.csvDownloadLimit;
    if(!limit) return;
    const visibleColumns = Object.entries(this.config.table)
      .filter(([, column]) => column.visible)
      .map(([, column]) => column);
    if(!visibleColumns.length) {
      this.loader.loading(true, {type: 'warn', message: this.lang.transform('one.payment')});
      return;
    }
    this.loader.loading(true);
    const headers = visibleColumns.map(col => this.lang.transform('smartTable.' + col.value));
    searchApplications(this.api, {offset: 0, limit, isPreload: false, pageToPreload: 0}, this.lastFilters, this.lastSort as SortQuery).then((res: any) => {
      const csvData = this.filterDuplicatePayment(res.result || []).map((request) => {
        this.payment.processOfflineData(request, false);
        const cells = this.tableDataTransformer.transformData(this.transformSingleRequest(request));
        const row: {[header: string]: string} = {};
        visibleColumns.forEach((col, index) => {
          const cellData = col.value ? cells[col.value] : undefined;
          row[headers[index]] = (col.value === 'isUrgent' || col.value === 'isDelayed')
            ? (cellData?.prefix ? 'Yes' : 'No')
            : (cellData?.value?.toString() || '');
        });
        return row;
      });
      this.loader.loading(false);
      if(!csvData.length) {
        this.loader.loading(true, {type: 'warn', message: this.lang.transform('one.payment')});
        return;
      }
      new ngxCsv(csvData, 'applications_report', {showTitle: true, title: 'Applications Report', headers});
    }).catch((err) => {
      this.loader.loading(true, {type: 'error', message: err});
    });
  }

  goToAllQr() {
    if(this.filteredData?.length === 0 || !this.filteredData) {
      this.loader.loading(true, {type: 'warn', message: this.lang.transform('one.payment')});
    } else {
      this.nav.to('admin-request-qr-list', undefined, {state: this.filteredData});
    }
  }

  private transformSingleRequest(request: RequestWithDuplicata): any {
    return {
      id: request.id,
      passportNumber: request.indexed.PassportNumber,
      currency: CurrencySign[request.currency as keyof typeof CurrencySign],
      firstName: request.indexed.FirstName,
      lastName: request.indexed.LastName,
      completeName: (request.indexed.FirstName || '') + ' ' + (request.indexed.LastName || ''),
      events: request.events,
      creationDate: request.createdAt ||  '',
      updatedOn: request.updatedOn || '',
      internalReference: request.internalReference || '',
      lock: (request.assigned_user__id && request.assignedAt) && differenceInHours(new Date(), new Date(request.assignedAt)) < 24,
      assignedAt: request.assignedAt || '',
      assignedUserId: request.assigned_user__id ? request.assigned_user__id : null,
      realState: request.state,
      price: request.amountCents / 100,
      operationId: request.operationId,
      transformedState: this.request.getStatusTheme(request).state,
      usageAfter: request.usageAfter ?? '',
      nationality: request.indexed.Nationality || '',
      usageUntil: request.usageUntil ?? '',
      iconColor: this.request.getStatusTheme(request).iconColor,
      isUrgent: this.isUrgent(request),
      isDelayed: this.isDelayed(request),
      batchId: request.batchId || '',
      documentType: this.lang.transform(request.document.id),
      duplicatas: request.duplicatas,
      watchlistsResponse: request.watchlistsResponse,
      apipnrResponse: request.apipnrResponse,
      fetchedAt: request.fetchedAt,
      timeslot: request.timeslot,
      timeslotPlaceId: request.timeslot_place_id || '',
      indicatifTelephoneNumber: request.indexed.IndicatifTelephoneNumber || '',
      phone: request.indexed.Phone || '',
      registrationPortId: request?.indexed?.RegistrationPortId,
      email: request?.indexed?.Email,
      paymentType: request.paymentType,
      meta: request.meta
    };
  }

  updateDataSource() {
    this.scanDocument.saveMultiple(this.filteredData, {history: true, watchlist: false, apipnr: false, inbound: false, fullMetadata: false});
    const transformedData: TransformedRequestWithDuplicata[] = this.filteredData.map(request => {
      this.payment.processOfflineData(request, false);
      return this.transformSingleRequest(request);
    });
    if(this.newFilter) {
      this.customTable();
    }
    if(!this.firstCall) this.firstCall = true;
    return transformedData;
  }

  launchFilter() {
    clearTimeout(this.timer);
    return new Promise((resolve) => {
      this.timer = setTimeout(() => {
        resolve('done');
      }, this.firstCall ? 500 : 0);
    });
  }

  filterDuplicatePayment(payment: RequestWithDuplicata[]) {
    const uniqueIds = new Set();
    return payment.filter(item => {
      if(!uniqueIds.has(item.id)) {
        uniqueIds.add(item.id);
        return true;
      }
      return false;
    });
  }

  customTable() {
    if(this.newFilter) {
      this.newFilter = false;
    }
  }

  isUrgent(payment: RequestWithDuplicata) {
    return payment.indexed?.Urgent === 'Yes';
  }

  isDelayed(payment: RequestWithDuplicata) {
    return this.request.isDelayed(payment);
  }

  navigateWithParams(row: any) {
    const request: RequestWithDuplicata = this.loadedData.find((data: RequestWithDuplicata) => data.id === row.applicationId.value);
    if(this.api.env.adminSelectRequestDisabled) return ;
    this.loader.loading(true);
    return this.checkAndLockPayment(request).then((response) => {
      if(response) {
        this.loader.loading(false);
        this.nav.to('admin-request-select/' + request.id, undefined, {
          queryParams: {...this.params, from: 'admin-request'}});
      }
    }).catch((err) => {
      console.log('error', err);
      this.loader.loading(true, {type: 'error', message: err});
    });
  }

  checkAndLockPayment(payment: RequestWithDuplicata): Promise<boolean> {
    if(this.api.userRole.isAdmin || !this.api.hasPerm(UserPermission.ALLOW_CONTROL)) {
      return Promise.resolve(true);
    }

    const lockPayment = () => this.api.userInfo.role === UserPlatformRole.EXTERNAL_MONITOR ? Promise.resolve(true) :
      this.api.lockPayment(payment.id, true, false, '', this.api.userPlaceId).then(() => true).catch((err) => {
        this.loader.loading(true, {type: 'error', message: err});
        return true;
      });

    if(!payment.assignedAt && !payment.assignedUserId) {
      return lockPayment();
    } else if(+payment.assignedUserId !== +this.api.userInfo.id) {
      const assignedTime = new Date(payment.assignedAt);
      const now = new Date();

      if(differenceInHours(now, assignedTime) < 2 && this.api.checkSeniority() < 3) {
        return this.loader.loading(true, {type: 'warn',
          message: this.lang.transform('payment.assigned.warning', {officerId: this.sync.userDisplay(+payment.assignedUserId)}),
          btnLabel: 'global.confirm',
          custom: {closeBtnLabel: 'global.no'}
        }).then(response => {
          if(response) {
            this.loader.loading(true);
            return lockPayment();
          } else {
            return false;
          }
        });
      } else {
        return lockPayment();
      }
    } else {
      return lockPayment();
    }
  }

  getApplications(pagination?: PaginationQuery, filters?: FilterWithType[], sort?: SortQuery): Promise<any> {
    this.lastFilters = filters || [];
    this.lastSort = sort;
    // Delegate to strategy to build query and perform search
    return searchApplications(this.api, pagination, filters, sort).then((res: any) => {
      this.totalCount = res.count;
      this.filteredData = [];
      this.dataFromDates = [...this.filteredData, ...res.result];
      this.filteredData = this.filterDuplicatePayment(this.dataFromDates);
      // Keep the data in memory for row on click navigation limited to the current page, plus the preload pages
      this.loadedData = this.filteredData.concat(this.loadedData).slice(0, (this.config.pagination.preloadPages * 2  + 1) * pagination.limit);

      const localTransformedData = this.updateDataSource();

      return localTransformedData;
    }).catch((err) => {
      this.loader.loading(false, {type: 'error', message: err});
      throw err;
    });
  }

  onQueryChange(e: {filters: FilterWithType[]; sort: SortQuery; pagination: PaginationQuery}): void {
    handleAdminRequestsOnQueryChange({
      e,
      config: this.config,
      tableDataTransformer: this.tableDataTransformer,
      smartTable: this.smartTable,
      loader: this.loader,
      getApplications: (pagination, filters, sort) => this.getApplications(pagination, filters, sort)
    });
  }

  onLoadingStateChange(state: {navigateToPreloadingPage: boolean, dataReady: boolean}): void {
    if(state.navigateToPreloadingPage) this.loader.loading(true);
    if(state.dataReady) this.loader.loading(false);
  }

  /**
   * Generates status filter options based on the provided parameters
   * @param isExtension - Whether to generate extension filter options (excluding certain states)
   * @returns An array of filter option objects
   */
  private generateStatusFilterOptions(isExtension: boolean): {value: string, name: string}[] {
    return enumToArrayObject(RequestStatus, true) // Start with basic state filter from enum
      .filter(item => {
        if(isExtension) { // Filter out status from extension
          return Number(item.value) !== RequestState.PAID &&
                 Number(item.value) !== RequestState.DUPLICATED &&
                 Number(item.value) !== RequestState.REFUNDED &&
                 Number(item.value) !== RequestState.USED &&
                 Number(item.value) !== RequestState.DELAY_PAY_NOT_CONFIRMED;

        }
        return true;
      })
      .flatMap((originalState: any) => {
        if(+originalState.value === RequestState.PAID_NOT_CONFIRMED) { // Process PAID_NOT_CONFIRMED state
          return isExtension ? [
            {name: RequestState.PAID_NOT_CONFIRMED + RequestSubState.REVISIONS_0, value: (originalState.value + '.' + RequestSubState.REVISIONS_0)},
            {name: RequestState.PAID_NOT_CONFIRMED + RequestSubState.REVISIONS_2, value: (originalState.value + '.' + RequestSubState.REVISIONS_2)}
          ] : [
            {name: RequestState.PAID_NOT_CONFIRMED + RequestSubState.REVISIONS_0, value: (originalState.value + '.' + RequestSubState.REVISIONS_0)},
            {name: RequestState.PAID_NOT_CONFIRMED + RequestSubState.REVISIONS_1, value: (originalState.value + '.' + RequestSubState.REVISIONS_1)},
            {name: RequestState.PAID_NOT_CONFIRMED + RequestSubState.REVISIONS_2, value: (originalState.value + '.' + RequestSubState.REVISIONS_2)}
          ];
        }
        if(+originalState.value === RequestState.DELAY_PAY_NOT_CONFIRMED) { // Process DELAY_PAY_NOT_CONFIRMED state
          return [
            {name: RequestState.DELAY_PAY_NOT_CONFIRMED + RequestSubState.REVISIONS_0, value: (originalState.value + '.' + RequestSubState.REVISIONS_0)}
          ];
        }
        if(+originalState.value === RequestState.USED && !isExtension) { // Process USED state (only for non-extension)
          return [
            {name: RequestState.USED, value: (originalState.value + '.' + 0)},
            {name: RequestState.USED + RequestSubState.CHECKED_IN, value: (originalState.value + '.' + RequestSubState.CHECKED_IN)},
            {name: RequestState.USED + RequestSubState.CHECKED_OUT, value: (originalState.value + '.' + RequestSubState.CHECKED_OUT)},
            {name: RequestState.USED + RequestSubState.REFUSED_IN, value: (originalState.value + '.' + RequestSubState.REFUSED_IN)},
            {name: RequestState.USED + RequestSubState.REFUSED_OUT, value: (originalState.value + '.' + RequestSubState.REFUSED_OUT)},
            {name: RequestState.USED + RequestSubState.CLOSED, value: (originalState.value + '.' + RequestSubState.CLOSED)}
          ];
        }
        return originalState;
      });
  }
  protected readonly UiPermission = UiPermission;
}
